import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PortalLayout from '../../components/PortalLayout';
import DynamicFieldInput from '../../components/DynamicFieldInput';
import {
  getCustomerTickets,
  createCustomerTicket,
  getCustomerProjects,
  getPublicProjectFields,
  errorMessage,
} from '../../services/customerApi';
import { useLanguage } from '../../i18n/LanguageContext';
import { fmtDate } from '../../utils/format';
import { Pill, STATUS_COLORS } from '../../components/NotionUI';
import Select from '../../components/Select';

const emptyForm = { project_id: '', subject: '', description: '' };

function PortalDashboard({ customer, onLogout }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  const fetchTickets = () => {
    setLoading(true);
    getCustomerTickets()
      .then((d) => setTickets(d.tickets || []))
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTickets();
    getCustomerProjects().then((d) => setProjects(d.projects || [])).catch(() => {});
  }, []);

  const handleProjectChange = (projectId) => {
    setForm((f) => ({ ...f, project_id: projectId }));
    setFieldValues({});
    const project = projects.find((p) => p.id === Number(projectId));
    if (!project) {
      setDynamicFields([]);
      return;
    }
    getPublicProjectFields(project.slug)
      .then((d) => setDynamicFields(d.fields || []))
      .catch(() => setDynamicFields([]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await createCustomerTicket({ ...form, field_values: fieldValues });
      setShowForm(false);
      setForm(emptyForm);
      setDynamicFields([]);
      setFieldValues({});
      if (res.ticket) {
        navigate(`/portal/tickets/${res.ticket.id}`);
      } else {
        fetchTickets();
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalLayout title={t('portal.dashboardTitle')} subtitle={customer?.name} companyName={t('app.title')} onLogout={onLogout}>
      {error && <div className="error-message">{error}</div>}

      {!showForm ? (
        <button
          type="button"
          className="btn portal-new"
          onClick={() => {
            setForm(emptyForm);
            setShowForm(true);
            handleProjectChange(projects[0]?.id || '');
          }}
          disabled={projects.length === 0}
        >
          + {t('portal.newRequest')}
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="card portal-gap">
          <div className="card-body">
            {projects.length > 1 && (
              <div className="form-group">
                <label>{t('portal.project')}</label>
                <Select value={form.project_id} onChange={(e) => handleProjectChange(Number(e.target.value))} required>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Select>
              </div>
            )}
            {dynamicFields
              .filter((field) => field.core_key !== 'name' && field.core_key !== 'email')
              .map((field) =>
                field.core_key ? (
                  <DynamicFieldInput
                    key={field.id}
                    field={field}
                    value={form[field.core_key]}
                    onChange={(value) => setForm({ ...form, [field.core_key]: value })}
                    locale={lang}
                  />
                ) : (
                  <DynamicFieldInput
                    key={field.id}
                    field={field}
                    value={fieldValues[field.id]}
                    onChange={(value) => setFieldValues({ ...fieldValues, [field.id]: value })}
                    locale={lang}
                  />
                )
              )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="btn" disabled={saving}>
                {saving ? t('portal.submitting') : t('portal.submit')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : tickets.length === 0 ? (
        <div className="portal-empty">{t('portal.noTickets')}</div>
      ) : (
        <div className="portal-tickets">
          {tickets.map((tk) => (
            <Link key={tk.id} to={`/portal/tickets/${tk.id}`} className="portal-ticket">
              <span className="portal-ticket-id">#{tk.id}</span>
              <span className="portal-ticket-main">
                <span className="portal-ticket-subject">{tk.subject}</span>
                <span className="portal-ticket-date">{fmtDate(tk.created_at)}</span>
              </span>
              <Pill dot color={STATUS_COLORS[tk.status]}>{t(`portal.status_${tk.status}`)}</Pill>
              <span className="portal-ticket-arrow" aria-hidden="true">›</span>
            </Link>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}

export default PortalDashboard;
